import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Clock, X, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const options = [
  { verb: "approve", label: "Approve", Icon: Check, color: "bg-[#8DB87A]" },
  { verb: "hold", label: "Hold", Icon: Clock, color: "bg-[#F0C464]" },
  { verb: "deny", label: "Disapprove", Icon: X, color: "bg-[#EC008C]" },
  { verb: "delete", label: "Delete account", Icon: Trash2, color: "bg-[#1A2030] text-white" },
];

export const VerificationActions = ({ detail, token, onUpdated, onDeleted }) => {
  const [action, setAction] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const deleting = action?.verb === "delete";
  const valid = deleting
    ? !!detail.email && value.trim().toLowerCase() === detail.email.toLowerCase()
    : action?.verb === "approve" || !!value.trim();

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setError("");
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (deleting) {
        await axios.delete(`${API}/admin/users/${encodeURIComponent(detail.user_id)}`, { ...config, params: { email_confirm: value.trim() } });
        toast.success("Account deleted");
        onDeleted();
      } else {
        await axios.post(`${API}/admin/verifications/${encodeURIComponent(detail.verification_id)}/${action.verb}`, { reason: value.trim() }, config);
        toast.success("Verification decision saved");
        await onUpdated();
      }
      setAction(null);
    } catch (e) {
      const message = e.response?.data?.detail;
      setError(typeof message === "string" ? message : message?.message || "Action failed. Please retry.");
    } finally { setBusy(false); }
  };

  return <>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {options.map(({ verb, label, Icon, color }) => <button key={verb}
        data-testid={`verif-${verb}-btn`} disabled={busy || (verb === "delete" && !detail.email)}
        onClick={() => { setAction({ verb, label }); setValue(""); setError(""); }}
        className={`min-h-11 rounded-lg px-2 text-xs font-bold text-black flex items-center justify-center gap-1 hover:brightness-110 transition-[filter] disabled:opacity-50 ${color}`}>
        <Icon className="w-4 h-4 shrink-0" />{label}
      </button>)}
    </div>
    <Dialog open={!!action} onOpenChange={(open) => { if (!open && !busy) setAction(null); }}>
      <DialogContent className="bg-[#0A0E18] text-white border-[#1A2030] max-w-[calc(100%-2rem)] sm:max-w-md" data-testid="verif-action-dialog">
        <DialogHeader>
          <DialogTitle data-testid="verif-action-title">{action?.label}</DialogTitle>
          <DialogDescription className="text-[#8A92A0] break-words" data-testid="verif-action-description">
            {deleting ? `Permanently delete ${detail.display_name}? Financial history prevents deletion. Type ${detail.email} to confirm.` : detail.display_name}
          </DialogDescription>
        </DialogHeader>
        <label htmlFor="verification-action-value" className="text-sm">{deleting ? "Account email" : action?.verb === "approve" ? "Note (optional)" : "Reason (required)"}</label>
        <Input id="verification-action-value" data-testid="verif-action-input" value={value} disabled={busy} maxLength={500}
          autoComplete="off" onChange={(e) => setValue(e.target.value)} className="bg-black border-[#1A2030]" />
        {error && <p role="alert" data-testid="verif-action-error" className="text-sm text-[#FF66BB] break-words">{error}</p>}
        <DialogFooter className="gap-2">
          <Button data-testid="verif-action-cancel" variant="outline" disabled={busy} onClick={() => setAction(null)}>Cancel</Button>
          <Button data-testid="verif-action-confirm" className="bg-[#EC008C] hover:bg-[#FF3399] text-black" disabled={!valid || busy} onClick={submit}>
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{deleting ? "Permanently delete" : "Confirm decision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
};